import { ExternalLink, Footprints, Coffee, Flower2, Leaf, ShoppingBag, Waves, MapPin } from "lucide-react";
import { thingKindLabel, type ThingKind, type ThingToDo } from "@/lib/thingsToDo";

const ICONS: Record<ThingKind, typeof Waves> = {
  surf: Waves,
  food: Coffee,
  yoga: Flower2,
  nature: Leaf,
  shop: ShoppingBag,
  other: MapPin,
};

/** Local places near an area or a collection's homes. Renders nothing for an empty list. */
const ThingsToDoList = ({ items, heading = "Things to do nearby", className }: { items: readonly ThingToDo[]; heading?: string; className?: string }) => {
  if (items.length === 0) return null;
  return (
    <section className={className}>
      <h2 className="font-serif text-2xl font-semibold text-foreground mb-5">{heading}</h2>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((t, i) => {
          const Icon = ICONS[t.kind];
          const body = (
            <>
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Icon className="h-4 w-4 text-primary" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-foreground leading-tight flex items-center gap-1.5">
                  <span className="truncate">{t.name}</span>
                  {t.url && <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />}
                </p>
                <p className="text-[13px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2">
                  <span>{thingKindLabel(t.kind)}</span>
                  {t.minutesAway != null && (
                    <span className="inline-flex items-center gap-1">
                      <Footprints className="h-3 w-3" aria-hidden /> {t.minutesAway} min
                    </span>
                  )}
                </p>
                {t.note && <p className="text-[13px] text-muted-foreground mt-1">{t.note}</p>}
              </div>
            </>
          );
          const cls = "flex items-start gap-3 rounded-card border border-border bg-card p-4";
          return (
            <li key={`${t.name}-${i}`}>
              {t.url ? (
                <a href={t.url} target="_blank" rel="noopener noreferrer" className={`${cls} hover:border-primary/50 transition-colors`}>
                  {body}
                </a>
              ) : (
                <div className={cls}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default ThingsToDoList;
